import { List, useTable, DeleteButton, ShowButton, EditButton } from '@refinedev/antd'
import {
  Table,
  Space,
  Tag,
  Input,
  Button,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Image,
  message,
  Tooltip,
  Select,
} from 'antd'
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  InstagramOutlined,
  FacebookOutlined,
  TwitterOutlined,
  SendOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import type { BaseRecord } from '@refinedev/core'
import { useInvalidate, useCustom } from '@refinedev/core'
import { useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const { Text } = Typography

type SocialPlatform = 'INSTAGRAM' | 'FACEBOOK' | 'TIKTOK' | 'TWITTER'
type SocialPostStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'CANCELLED'

interface SocialPost extends BaseRecord {
  id: string
  content: string
  thumbnailUrl: string | null
  platforms: SocialPlatform[]
  scheduledAt: string | null
  publishedAt: string | null
  status: SocialPostStatus
  createdAt: string
}

interface PostStats {
  total: number
  draft: number
  scheduled: number
  published: number
  failed: number
}

const statusConfig: Record<SocialPostStatus, { color: string; text: string; icon: React.ReactNode }> = {
  DRAFT: { color: 'default', text: 'Nacrt', icon: <ClockCircleOutlined /> },
  SCHEDULED: { color: 'blue', text: 'Zakazano', icon: <ClockCircleOutlined /> },
  PUBLISHING: { color: 'processing', text: 'Objavljuje se', icon: <SendOutlined /> },
  PUBLISHED: { color: 'success', text: 'Objavljeno', icon: <CheckCircleOutlined /> },
  FAILED: { color: 'error', text: 'Neuspelo', icon: <ExclamationCircleOutlined /> },
  CANCELLED: { color: 'warning', text: 'Otkazano', icon: <ExclamationCircleOutlined /> },
}

const platformIcons: Record<SocialPlatform, React.ReactNode> = {
  INSTAGRAM: <InstagramOutlined style={{ color: '#E4405F' }} />,
  FACEBOOK: <FacebookOutlined style={{ color: '#1877F2' }} />,
  TIKTOK: <span style={{ fontSize: 14 }}>TT</span>,
  TWITTER: <TwitterOutlined style={{ color: '#1DA1F2' }} />,
}

const platformLabels: Record<SocialPlatform, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  TIKTOK: 'TikTok',
  TWITTER: 'X (Twitter)',
}

export function SocialMediaListPage() {
  const invalidate = useInvalidate()
  const [statusFilter, setStatusFilter] = useState<SocialPostStatus | undefined>()

  const { tableProps, searchFormProps } = useTable<SocialPost>({
    resource: 'social-media',
    syncWithLocation: true,
    filters: {
      permanent: statusFilter ? [{ field: 'status', operator: 'eq', value: statusFilter }] : [],
    },
    onSearch: (values) => {
      const searchValues = values as { search?: string }
      return [
        {
          field: 'search',
          operator: 'contains',
          value: searchValues.search,
        },
      ]
    },
  })

  // Fetch stats
  const { query: statsQuery } = useCustom<PostStats>({
    url: '/admin/social-media/posts/stats',
    method: 'get',
  })

  const stats = statsQuery.data?.data

  // Publish post immediately
  const handlePublishNow = async (id: string) => {
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/social-media/posts/${id}/publish`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Objavljivanje nije uspelo')
      }

      message.success('Post je poslat na objavljivanje')
      invalidate({ resource: 'social-media', invalidates: ['list'] })
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo objavljivanje'}`)
    }
  }

  return (
    <List
      title="Drustvene Mreze"
      headerButtons={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => invalidate({ resource: 'social-media', invalidates: ['list'] })}
          >
            Osvezi
          </Button>
          <Link to="/social-media/calendar">
            <Button icon={<CalendarOutlined />}>Kalendar</Button>
          </Link>
          <Link to="/social-media/accounts">
            <Button>Povezani nalozi</Button>
          </Link>
          <Link to="/social-media/create">
            <Button type="primary" icon={<PlusOutlined />}>
              Novi post
            </Button>
          </Link>
        </Space>
      }
    >
      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small" hoverable onClick={() => setStatusFilter(undefined)}>
            <Statistic title="Ukupno" value={stats?.total || 0} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" hoverable onClick={() => setStatusFilter('DRAFT')}>
            <Statistic
              title="Nacrti"
              value={stats?.draft || 0}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" hoverable onClick={() => setStatusFilter('SCHEDULED')}>
            <Statistic
              title="Zakazano"
              value={stats?.scheduled || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" hoverable onClick={() => setStatusFilter('PUBLISHED')}>
            <Statistic
              title="Objavljeno"
              value={stats?.published || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <form {...searchFormProps}>
            <Space>
              <Input
                placeholder="Pretrazi sadrzaj..."
                prefix={<SearchOutlined />}
                style={{ width: 250 }}
                name="search"
                allowClear
              />
              <Button type="primary" htmlType="submit">
                Pretrazi
              </Button>
            </Space>
          </form>
          <Select
            placeholder="Status"
            style={{ width: 150 }}
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'DRAFT', label: 'Nacrt' },
              { value: 'SCHEDULED', label: 'Zakazano' },
              { value: 'PUBLISHED', label: 'Objavljeno' },
              { value: 'FAILED', label: 'Neuspelo' },
            ]}
          />
        </Space>
      </Card>

      {/* Table */}
      <Table {...tableProps} rowKey="id" scroll={{ x: 800 }}>
        <Table.Column
          dataIndex="thumbnailUrl"
          title=""
          width={60}
          render={(url: string | null) =>
            url ? (
              <Image
                src={url}
                width={40}
                height={40}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                preview={false}
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  backgroundColor: '#f0f0f0',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SendOutlined style={{ color: '#bfbfbf' }} />
              </div>
            )
          }
        />
        <Table.Column
          dataIndex="content"
          title="Sadrzaj"
          ellipsis
          render={(content: string, record: SocialPost) => (
            <div>
              <Link to={`/social-media/${record.id}`}>
                <Text strong style={{ color: '#1677ff', cursor: 'pointer' }}>
                  {content.length > 80 ? `${content.substring(0, 80)}...` : content}
                </Text>
              </Link>
            </div>
          )}
        />
        <Table.Column
          dataIndex="platforms"
          title="Platforme"
          render={(platforms: SocialPlatform[]) => (
            <Space size={4}>
              {platforms.map((platform) => (
                <Tooltip key={platform} title={platformLabels[platform]}>
                  <Tag>{platformIcons[platform]}</Tag>
                </Tooltip>
              ))}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: SocialPostStatus) => {
            const config = statusConfig[status]
            return (
              <Tag color={config.color} icon={config.icon}>
                {config.text}
              </Tag>
            )
          }}
        />
        <Table.Column
          dataIndex="scheduledAt"
          title="Zakazano za"
          render={(date: string | null) =>
            date ? (
              <Text>{new Date(date).toLocaleString('sr-Latn-RS')}</Text>
            ) : (
              <Text type="secondary">-</Text>
            )
          }
        />
        <Table.Column
          dataIndex="publishedAt"
          title="Objavljeno"
          render={(date: string | null) =>
            date ? (
              <Text>{new Date(date).toLocaleString('sr-Latn-RS')}</Text>
            ) : (
              <Text type="secondary">-</Text>
            )
          }
        />
        <Table.Column
          dataIndex="createdAt"
          title="Kreirano"
          render={(date: string) => new Date(date).toLocaleDateString('sr-Latn-RS')}
        />
        <Table.Column
          title="Akcije"
          fixed="right"
          width={180}
          render={(_, record: SocialPost) => (
            <Space size="small">
              <ShowButton hideText size="small" recordItemId={record.id} />
              {record.status === 'DRAFT' && (
                <>
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <Tooltip title="Objavi odmah">
                    <Button
                      size="small"
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={() => handlePublishNow(record.id)}
                    />
                  </Tooltip>
                </>
              )}
              {record.status !== 'PUBLISHED' && (
                <DeleteButton hideText size="small" recordItemId={record.id} />
              )}
            </Space>
          )}
        />
      </Table>
    </List>
  )
}

export { default as SocialMediaCreatePage } from './create'
export { default as SocialMediaShowPage } from './show'
export { default as SocialMediaAccountsPage } from './accounts'
export { default as SocialMediaCallbackPage } from './callback'
export { default as SocialMediaCalendarPage } from './calendar'
